import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { useCustomerMerge } from "@/hooks/useCustomerMerge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Customer } from "@/hooks/useCustomers";
import { GitMerge, AlertTriangle, Check } from "lucide-react";

/* ── Field config ─────────────────────────────────────────────────── */

const INLINE_FIELDS = [
  { key: "name", label: "Company Name" },
  { key: "contact_name", label: "Contact Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "customer_type", label: "Type" },
  { key: "billing_address", label: "Billing Address" },
  { key: "billing_city", label: "Billing City" },
  { key: "billing_state", label: "Billing State" },
  { key: "billing_zip", label: "Billing Zip" },
  { key: "job_address", label: "Job Address" },
  { key: "job_city", label: "Job City" },
  { key: "job_state", label: "Job State" },
  { key: "job_zip", label: "Job Zip" },
] as const;

const ALL_FIELD_KEYS = [...INLINE_FIELDS.map((f) => f.key), "notes"] as const;
type FieldKey = (typeof ALL_FIELD_KEYS)[number];

/* ── Impact helpers ───────────────────────────────────────────────── */

interface ImpactCounts {
  projects: number;
  estimates: number;
  invoices: number;
  contacts: number;
}

async function fetchImpactCounts(customerId: string): Promise<ImpactCounts> {
  const [projects, estimates, invoices, contacts] = await Promise.all([
    supabase.from("projects").select("id", { count: "exact", head: true }).eq("customer_id", customerId),
    supabase.from("estimates").select("id", { count: "exact", head: true }).eq("customer_id", customerId),
    supabase.from("invoices").select("id", { count: "exact", head: true }).eq("customer_id", customerId),
    supabase.from("customer_contacts").select("id", { count: "exact", head: true }).eq("customer_id", customerId),
  ]);
  return {
    projects: projects.count ?? 0,
    estimates: estimates.count ?? 0,
    invoices: invoices.count ?? 0,
    contacts: contacts.count ?? 0,
  };
}

function total(c: ImpactCounts) {
  return c.projects + c.estimates + c.invoices + c.contacts;
}

function CountsBadge({ counts }: { counts: ImpactCounts | undefined }) {
  if (!counts) return null;
  const parts: string[] = [];
  if (counts.projects > 0) parts.push(`${counts.projects} proj`);
  if (counts.estimates > 0) parts.push(`${counts.estimates} est`);
  if (counts.invoices > 0) parts.push(`${counts.invoices} inv`);
  if (counts.contacts > 0) parts.push(`${counts.contacts} cont`);
  if (parts.length === 0) return <span className="text-xs text-muted-foreground ml-1">(no records)</span>;
  return <span className="text-xs text-muted-foreground ml-1">({parts.join(", ")})</span>;
}

/* ── Main component ───────────────────────────────────────────────── */

interface CustomerMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerA: Customer;
  customerB: Customer;
}

export const CustomerMergeDialog = ({ open, onOpenChange, customerA, customerB }: CustomerMergeDialogProps) => {
  const { user } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const merge = useCustomerMerge();

  // Fetch impact counts for BOTH customers
  const { data: impactBoth } = useQuery({
    queryKey: ["merge_impact_both", customerA.id, customerB.id],
    queryFn: async () => {
      const [a, b] = await Promise.all([fetchImpactCounts(customerA.id), fetchImpactCounts(customerB.id)]);
      return { a, b };
    },
    enabled: open && !!user,
  });

  // Auto-recommend the customer with more linked records
  const recommendedId = useMemo(() => {
    if (!impactBoth) return customerA.id;
    return total(impactBoth.b) > total(impactBoth.a) ? customerB.id : customerA.id;
  }, [impactBoth, customerA.id, customerB.id]);

  const [winnerId, setWinnerId] = useState(customerA.id);
  const [fieldChoices, setFieldChoices] = useState<Record<FieldKey, "a" | "b">>(() => {
    const defaults: Record<string, "a" | "b"> = {};
    for (const k of ALL_FIELD_KEYS) defaults[k] = "a";
    return defaults as Record<FieldKey, "a" | "b">;
  });

  // Update winnerId when recommendation resolves
  useEffect(() => {
    if (impactBoth) setWinnerId(recommendedId);
  }, [recommendedId, impactBoth]);

  const loserId = winnerId === customerA.id ? customerB.id : customerA.id;
  const winner = winnerId === customerA.id ? customerA : customerB;
  const loser = winnerId === customerA.id ? customerB : customerA;
  const loserCounts = impactBoth ? (loserId === customerA.id ? impactBoth.a : impactBoth.b) : undefined;
  const winnerCounts = impactBoth ? (winnerId === customerA.id ? impactBoth.a : impactBoth.b) : undefined;

  const showDirectionWarning = useMemo(() => {
    if (!impactBoth || !loserCounts || !winnerCounts) return false;
    return winnerId !== recommendedId && total(loserCounts) > total(winnerCounts);
  }, [winnerId, recommendedId, impactBoth, loserCounts, winnerCounts]);

  // Build overrides
  const overrides = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const k of ALL_FIELD_KEYS) {
      const chosen = fieldChoices[k] === "a" ? customerA : customerB;
      if (chosen.id !== winnerId) result[k] = (chosen as any)[k];
    }
    return result;
  }, [fieldChoices, winnerId, customerA, customerB]);

  const handleMerge = async () => {
    try {
      await merge.mutateAsync({ winnerId, loserId, overrides });
      toast.success(`Merged into ${winner.name}`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Merge failed");
    }
  };

  const totalImpactLoser = loserCounts ? total(loserCounts) : 0;
  const notesA = customerA.notes || "";
  const notesB = customerB.notes || "";
  const hasNotes = !!(notesA || notesB);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-4 pt-4 pb-3 sm:px-6 sm:pt-6 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base">
              <GitMerge className="h-5 w-5 shrink-0" /> Merge Customers
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Choose which customer to keep. The other will be archived and all its records moved.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 space-y-4">
            {/* ── Winner selection ───────────────────────────── */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                Keep which customer?
              </Label>
              <RadioGroup value={winnerId} onValueChange={setWinnerId} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[customerA, customerB].map((c, i) => {
                  const side = i === 0 ? "a" : "b";
                  const isWinner = winnerId === c.id;
                  const isRecommended = impactBoth && recommendedId === c.id;
                  return (
                    <label
                      key={c.id}
                      className={`relative flex flex-col gap-1 border rounded-lg px-3 py-2.5 cursor-pointer transition-all ${
                        isWinner
                          ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value={c.id} className="shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">
                          #{c.customer_number} {c.name}
                        </span>
                        {isWinner && <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-auto" />}
                      </div>
                      <div className="flex items-center gap-2 pl-6">
                        <CountsBadge counts={i === 0 ? impactBoth?.a : impactBoth?.b} />
                        {isRecommended && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Recommended</Badge>
                        )}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            {/* ── Direction warning ─────────────────────────── */}
            {showDirectionWarning && loserCounts && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-xs sm:text-sm text-foreground">
                  <strong>Warning:</strong> You're keeping the record with fewer linked items.{" "}
                  <strong>#{loser.customer_number} {loser.name}</strong> has{" "}
                  {loserCounts.projects} project(s), {loserCounts.invoices} invoice(s) that will be reassigned.
                </p>
              </div>
            )}

            {/* ── Field comparison table ────────────────────── */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                Choose field values
              </Label>
              <div className="border rounded-lg overflow-hidden">
                {/* Header — hidden on very small screens, shown as column labels */}
                <div className="hidden sm:grid grid-cols-[140px_1fr_1fr] bg-muted px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Field</span>
                  <span className="truncate">#{customerA.customer_number} {customerA.name}</span>
                  <span className="truncate">#{customerB.customer_number} {customerB.name}</span>
                </div>

                {INLINE_FIELDS.map((f) => {
                  const valA = (customerA as any)[f.key] || "—";
                  const valB = (customerB as any)[f.key] || "—";
                  const isDifferent = valA !== valB;
                  const choiceA = fieldChoices[f.key] === "a";
                  const choiceB = fieldChoices[f.key] === "b";

                  return (
                    <div
                      key={f.key}
                      className={`border-t ${isDifferent ? "bg-primary/5" : ""}`}
                    >
                      {/* Mobile: stacked layout */}
                      <div className="sm:hidden px-3 py-2 space-y-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{f.label}</span>
                        <label className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 -mx-1 transition-colors ${choiceA ? "bg-primary/10" : ""}`}>
                          <input type="radio" name={f.key} checked={choiceA} onChange={() => setFieldChoices((p) => ({ ...p, [f.key]: "a" }))} className="accent-primary shrink-0" />
                          <span className="text-sm text-foreground break-words">{valA}</span>
                        </label>
                        <label className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 -mx-1 transition-colors ${choiceB ? "bg-primary/10" : ""}`}>
                          <input type="radio" name={f.key} checked={choiceB} onChange={() => setFieldChoices((p) => ({ ...p, [f.key]: "b" }))} className="accent-primary shrink-0" />
                          <span className="text-sm text-foreground break-words">{valB}</span>
                        </label>
                      </div>

                      {/* Desktop: row layout */}
                      <div className="hidden sm:grid grid-cols-[140px_1fr_1fr] px-3 py-1.5 text-sm items-center">
                        <span className="text-muted-foreground text-xs font-medium">{f.label}</span>
                        <label className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 -mx-1 transition-colors ${choiceA ? "bg-primary/10" : ""}`}>
                          <input type="radio" name={f.key} checked={choiceA} onChange={() => setFieldChoices((p) => ({ ...p, [f.key]: "a" }))} className="accent-primary shrink-0" />
                          <span className="truncate text-foreground">{valA}</span>
                        </label>
                        <label className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 -mx-1 transition-colors ${choiceB ? "bg-primary/10" : ""}`}>
                          <input type="radio" name={f.key} checked={choiceB} onChange={() => setFieldChoices((p) => ({ ...p, [f.key]: "b" }))} className="accent-primary shrink-0" />
                          <span className="truncate text-foreground">{valB}</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Notes (full-width, not in the cramped grid) ── */}
            {hasNotes && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Notes
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { side: "a" as const, customer: customerA, val: notesA },
                    { side: "b" as const, customer: customerB, val: notesB },
                  ].map(({ side, customer, val }) => {
                    const isChosen = fieldChoices.notes === side;
                    return (
                      <label
                        key={side}
                        onClick={() => setFieldChoices((p) => ({ ...p, notes: side }))}
                        className={`relative flex flex-col gap-1 border rounded-lg p-3 cursor-pointer transition-all ${
                          isChosen
                            ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="radio"
                            name="notes"
                            checked={isChosen}
                            onChange={() => setFieldChoices((p) => ({ ...p, notes: side }))}
                            className="accent-primary shrink-0"
                          />
                          <span className="text-xs font-medium text-muted-foreground">
                            #{customer.customer_number} {customer.name}
                          </span>
                          {isChosen && <Check className="h-3 w-3 text-primary ml-auto shrink-0" />}
                        </div>
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words pl-5 max-h-28 overflow-y-auto">
                          {val || <span className="text-muted-foreground italic">No notes</span>}
                        </p>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Impact summary ────────────────────────────── */}
            {loserCounts && totalImpactLoser > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted border border-border">
                <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-xs sm:text-sm text-foreground">
                  <p className="font-medium">Will be moved to {winner.name}:</p>
                  <ul className="mt-1 text-muted-foreground list-disc ml-4">
                    {loserCounts.projects > 0 && <li>{loserCounts.projects} project(s)</li>}
                    {loserCounts.estimates > 0 && <li>{loserCounts.estimates} estimate(s)</li>}
                    {loserCounts.invoices > 0 && <li>{loserCounts.invoices} invoice(s)</li>}
                    {loserCounts.contacts > 0 && <li>{loserCounts.contacts} contact(s)</li>}
                  </ul>
                  <p className="mt-1 text-muted-foreground">
                    <strong>#{loser.customer_number} {loser.name}</strong> will be archived.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer ──────────────────────────────────────── */}
          <div className="flex justify-end gap-2 px-4 py-3 sm:px-6 border-t border-border">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirmOpen(true)} disabled={merge.isPending}>
              <GitMerge className="mr-1 h-4 w-4" /> Merge
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Merge</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently merge <strong>#{loser.customer_number} {loser.name}</strong> into{" "}
              <strong>#{winner.customer_number} {winner.name}</strong>. All projects, invoices, estimates,
              and contacts will be reassigned. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMerge} disabled={merge.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {merge.isPending ? "Merging…" : "Confirm Merge"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
